import { motion } from "framer-motion";
import Masonry from "react-masonry-css";

import {
  Bell,
  Package,
  Users,
  ShoppingCart,
  FileText,
  Building,
  AlertTriangle,
  ShoppingBagIcon,
  TagIcon,
  PackageOpenIcon,
  Building2Icon,
  TruckIcon,
  MapIcon,
} from "lucide-react";
import { Card, Badge, ScrollShadow, Chip, Avatar } from "@heroui/react";
import OrderGraph from "../components/orderGraph";
import TimeDisplay from "./components/date";
import AnimatedCounter from "./components/counter";
import OrderAnimatedCounter from "./components/counterv2";
import WeatherCard from "./components/weather";
import DeliveryMap from "./components/deliveryMap";
import { useEffect, useState } from 'react';
import api from "../../api/api";
import { useNavigate } from 'react-router-dom';
import Loading from "../components/loading";

// Variantes d'animation pour chaque section
const bentoVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

// Conteneur avec animation de décalage entre les enfants
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.2 } },
};

// Fake Data pour le Dashboard
const fakeData = {
  notifications: [
    { id: 1, message: "Nouvelle commande reçue", read: false },
    { id: 2, message: "Stock faible sur un produit", read: false },
    { id: 3, message: "Stock faible sur un produit", read: false },
    { id: 4, message: "Stock faible sur un produit", read: false },
    { id: 5, message: "Stock faible sur un produit", read: false },
    { id: 6, message: "Stock faible sur un produit", read: false },
    { id: 7, message: "Stock faible sur un produit", read: false },
    { id: 8, message: "Stock faible sur un produit", read: false },
    { id: 9, message: "Stock faible sur un produit", read: false },
    { id: 10, message: "Stock faible sur un produit", read: false },
  ],
  orders: [
    { id: "ORD123", status: "pending" },
    { id: "ORD124", status: "shipped" },
    { id: "ORD125", status: "delivered" },
    { id: "ORD126", status: "delivered" },
    { id: "ORD127", status: "delivered" },
    { id: "ORD128", status: "delivered" },
  ],
  clientsInd: [
    { id: "CLI1", name: "Jean Dupont" },
    { id: "CLI2", name: "Marie Curie" },
    { id: "CLI3", name: "Paul Martin" },
    { id: "CLI4", name: "Alice Durand" },
    { id: "CLI5", name: "Luc Bernard" },
  ],
  clientsEnt: [
    { id: "ENT1", name: "Tesla Inc." },
    { id: "ENT2", name: "Amazon France" },
    { id: "ENT3", name: "Google France" },
    { id: "ENT4", name: "Microsoft" },
    { id: "ENT5", name: "Airbus" },
  ],
  carts: [
    { id: "CART1", total: "89.99€" },
    { id: "CART2", total: "45.50€" },
    { id: "CART3", total: "32.00€" },
    { id: "CART4", total: "12.75€" },
  ],
  logs: [
    { id: "LOG1", message: "Utilisateur connecté", type: "info" },
    { id: "LOG2", message: "Erreur sur le serveur", type: "error" },
    { id: "LOG3", message: "Base de données non réactive", type: "error" },
    { id: "LOG4", message: "Débit de connexion faible", type: "error" },
    { id: "LOG5", message: "Mise à jour réussie", type: "info" },
    { id: "LOG6", message: "Erreur sur le serveur", type: "error" },
  ],
};

// Fonction pour obtenir la couleur des badges en fonction du status
const getStatusColor = (status) => {
  switch (status) {
    case "pending":
      return "warning";
    case "confirmed":
      return "primary";
    case "shipped":
      return "secondary";
    case "delivered":
      return "success";
    case "canceled":
      return "danger";
    case "refunded":
      return "default";
    case "failed":
      return "error";
    default:
      return "gray";
  }
};

// Composant Principal du Dashboard avec un design bento non uniforme
export default function DashboardBento() {

  const [order, setOrder] = useState(null)
  const [delivery, setDelivery] = useState(null)
  const [category, setCategory] = useState(null)
  const [product, setProduct] = useState(null)
  const [enterprise, setEnterprise] = useState(null)
  const [particular, setParticular] = useState(null)
  const [basket, setBasket] = useState(null)
  const [notification, setNotification] = useState(null)
  const [logs, setLogs] = useState(null)

  const nav = useNavigate()

  useEffect(()=>{

    const fetchState = async ()=>{

      try {

        const data = await api.admin.states()
        console.log(data)

        setOrder(data.order)
        setDelivery(data.delivery)
        setCategory(data.category)
        setProduct(data.products)
        setEnterprise(data.enterprise)
        setParticular(data.particular)
        setBasket(data.basket)
        setNotification(data.notification)
        setLogs(data.logs)
        
      } catch (error) {
        
        console.log(error)

      }
      
    }

    fetchState()

  }, [])

  const breakpointColumnsObj = {
    default: 4,  // Nombre de colonnes par défaut
    1100: 3,     // 3 colonnes lorsque la largeur est <= 1100px
    768: 2,      // 2 colonnes lorsque la largeur est <= 768px
    500: 1       // 1 colonne lorsque la largeur est <= 500px
  };
  

  return (
      <div className="p-2">

        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-12 gap-2 content-start"
        >
        
          <motion.div
              variants={bentoVariants}
              className="col-span-12 lg:col-span-6 row-span-1"
          >
            <TimeDisplay />
          </motion.div>

          <motion.div
              variants={bentoVariants}
              className="col-span-12 lg:col-span-6 row-span-1"
          >
            <WeatherCard />
          </motion.div>

          {/* Animated Counter (petit) */}
          <motion.div
              variants={bentoVariants}
              className="col-span-6 lg:col-span-2"
          >
            {order && <AnimatedCounter title="Commandes" target={order.nb_order} Icon={ShoppingBagIcon} />}
          </motion.div>

          <motion.div
              variants={bentoVariants}
              className="col-span-6 lg:col-span-2"
              >
            {product && <AnimatedCounter title="Produit" target={product.nb_product} Icon={Package}/>}
          </motion.div>

          <motion.div variants={bentoVariants} className="col-span-6 lg:col-span-2">
            {category && <AnimatedCounter title="Categories" target={category.nb_category} Icon={TagIcon}/>}
          </motion.div>

          <motion.div variants={bentoVariants} className="col-span-6 lg:col-span-2">
            {delivery && <AnimatedCounter title="Livraison" target={delivery.nb_delivery} Icon={PackageOpenIcon}/>}
          </motion.div>

          <motion.div variants={bentoVariants} className="col-span-6 lg:col-span-2">
            {enterprise && <AnimatedCounter title="entreprise" target={enterprise.nb_enterprise} Icon={Building2Icon}/>}
          </motion.div>

          <motion.div variants={bentoVariants} className="col-span-6 lg:col-span-2">
            {particular && <AnimatedCounter title="particular" target={particular.nb_particular} Icon={Users}/>}
          </motion.div>

          


          {/* Notifications (petit) */}
          {notification ? <motion.div
              variants={bentoVariants}
              className="col-span-12 lg:col-span-6 row-span-1"
          >
            <Card shadow="sm" className="p-4 h-full dark:bg-zinc-900">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">📌 Notifications</h3>
                <Bell size={20} className="text-gray-500" />
              </div>
              <ScrollShadow>
                {notification.un_read_notification.map((notif) => (
                    <div
                        key={notif.id}
                        className={`flex items-center py-1 px-2 border-b last:border-none justify-between ${notif.target_type ? "cursor-pointer" : "cursor-not-allowed"} hover:bg-primary hover:bg-opacity-10 hover:text-primary hover:rounded-lg`}
                    >
                      
                      <span className={`text-sm`}>{notif.message}</span>

                      <div
                          className={`w-2 h-2 rounded-full ${notif.is_read && "bg-primary"}`}
                      />
                    </div>
                ))}
              </ScrollShadow>
            </Card>
          </motion.div> : <div className="h-[30vh] "><Loading icon={<Bell className="text-primary"/>}/></div>}

          {/* Dernières Commandes (moyen) */}
          <motion.div
              variants={bentoVariants}
              className="col-span-12 lg:col-span-6 row-span-1"
          >
            <ScrollShadow>
              <Card shadow="sm" className="p-4 h-full dark:bg-zinc-900">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">Dernières Commandes</h3>
                  <Package size={20} className="text-gray-500" />
                </div>
                {order ? <div className="space-y-1">
                  {order.last_order.map((order) => (
                      <div
                          key={order.id}
                          className="flex justify-between items-center py-1 px-2 border-b last:border-none cursor-pointer hover:bg-primary hover:bg-opacity-10 hover:text-primary hover:rounded-lg"
                          onClick={()=>nav('/dashboard/orders/' + order.id)}
                      >
                        <span className="text-sm">Commande {order.id}</span>
                        <Chip color={getStatusColor(order.status)}>
                          {order.status}
                        </Chip>
                      </div>
                  ))}
                </div> : <div className="h-[30vh] "><Loading icon={<Package className="text-primary"/>}/></div>} 
              </Card>
            </ScrollShadow>
          </motion.div>

          {/* Clients Particuliers (moyen) */}
          <motion.div
              variants={bentoVariants}
              className="col-span-12 md:col-span-6 row-span-1"
          >
            <ScrollShadow className="h-[40vh]">
              <Card shadow="sm" className="p-4 h-full shadow-none dark:bg-zinc-900">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">Clients Particuliers</h3>
                  <Users size={20} className="text-gray-500" />
                </div>
                {particular ? <div className="space-y-1">
                  {particular.last_particular.map((client) => (
                      <div 
                        key={client.id} 
                        className="flex justify-between items-center py-1 px-2 border-b last:border-none cursor-pointer hover:bg-primary hover:bg-opacity-10 hover:text-primary hover:rounded-lg"
                        onClick={()=>nav("/dashboard/user/" + client.id)}
                      >
                        <span className="text-sm">{client.firstname} {client.lastname}</span>
                        <span className="text-sm">{client.email}</span>
                      </div>
                  ))}
                </div> : <div className="h-[30vh] "><Loading icon={<Package className="text-primary"/>}/></div>}
              </Card>
            </ScrollShadow>
          </motion.div>

          <motion.div
              variants={bentoVariants}
              className="col-span-12 md:col-span-6 row-span-1"
          >
            <ScrollShadow className="h-[40vh] shadow-none">
              <Card shadow="sm" className="p-4 h-full dark:bg-zinc-900">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">Clients Entreprises</h3>
                  <Building size={20} className="text-gray-500" />
                </div>
                {enterprise ? <div className="space-y-1">
                  {enterprise.last_enterprise.map((client) => (
                      <div 
                        key={client.id} 
                        className="flex justify-between items-center py-1 px-2 border-b last:border-none cursor-pointer hover:bg-primary hover:bg-opacity-10 hover:text-primary hover:rounded-lg"
                        onClick={()=>nav("/dashboard/user/" + client.id)}
                      >
                        <span className="text-sm">{client.firstname} {client.lastname}</span>
                        <span className="text-sm">{client.email}</span>
                      </div>
                  ))}
                </div> : <div className="h-[30vh] "><Loading icon={<Package className="text-primary"/>}/></div>}
              </Card>
            </ScrollShadow>
          </motion.div>

          {/* Derniers produits (moyen) */}          
          <motion.div
              variants={bentoVariants}
              className="col-span-12 lg:col-span-6 row-span-1"
          >
            <ScrollShadow className="shadow-none">

              <Card shadow="sm" className="p-4 h-min-[40vh] dark:bg-zinc-900">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">Derniers produits</h3>
                  <Package size={20} className="text-gray-500" />
                </div>
                {product ? <div className="space-y-1">
                  {product.last_product.map((cart) => {
                    
                    return cart.product_type === "particular" ? (

                      <div 
                        key={cart.id} 
                        className="py-1 px-2 border-b last:border-none flex justify-between items-center cursor-pointer hover:bg-primary hover:bg-opacity-10 hover:text-primary hover:rounded-lg overflow-auto"
                        onClick={()=>nav("/dashboard/product/" + cart.id)}
                      >

                        <div className=" flex-row justify-start items-center gap-4">

                          <span className="text-sm">
                            {cart.name}
                          </span>

                        </div>

                        <div className="text-sm space-x-4">
                          <span className={`${cart.discount_price && 'line-through text-gray-400'}`}>
                            € {cart.price}
                          </span>

                          <span className={`text-primary font-bold`}>
                            € {cart.discount_price && cart.discount_price}
                          </span>
                        </div>
                      </div>
                    ) : (

                      <div 
                        key={cart.id} 
                        className="py-1 px-2 border-b last:border-none flex justify-between items-center cursor-pointer hover:bg-primary hover:bg-opacity-10 hover:text-primary hover:rounded-lg overflow-auto"
                        onClick={()=>nav("/dashboard/product/" + cart.id)}
                      >
                        <span className="text-sm">
                          {cart.name}
                        </span>

                        <div className="text-sm space-x-4">
                          <span className={`${cart.pricing[0].discount_price && 'line-through text-gray-400'}`}>
                            € {cart.pricing[0].price}
                          </span>

                          <span className={`text-primary font-bold`}>
                            € {cart.pricing[0].discount_price && cart.pricing[0].discount_price}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>  : <div className="h-[30vh] "><Loading icon={<Package className="text-primary"/>}/></div>}
              </Card>
            </ScrollShadow>
          </motion.div>

          <motion.div
              variants={bentoVariants}
              className="col-span-12 lg:col-span-6 row-span-1"
          >
            <ScrollShadow>
              <Card shadow="sm" className="p-4 h-min-[40vh] dark:bg-zinc-900">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">Derniers Paniers</h3>
                  <ShoppingCart size={20} className="text-gray-500" />
                </div>
                {basket ? <div className="space-y-1">
                  {basket.last_basket.map((cart) => (
                      <div 
                        key={cart.id} 
                        className="py-1 px-2 border-b last:border-none flex justify-between items-center cursor-not-allowed hover:bg-gray hover:bg-opacity-10 hover:rounded-lg"
                        // onClick={()=>nav("/dashboard/product/" + cart.id)}
                      >
                        <span className="text-sm">
                          {cart.id}
                        </span>

                        <div className="text-sm space-x-4">
                          <span>
                            {cart.total_amount}
                          </span>

                        </div>
                      </div>
                  ))}
                </div> : <div className="h-[30vh] "><Loading icon={<Package className="text-primary"/>}/></div>}
              </Card>
            </ScrollShadow>
          </motion.div>

          <motion.div
              variants={bentoVariants}
              className="col-span-12 lg:col-span-12 row-span-12"
          >
            <Card shadow="sm" className="p-4 h-full dark:bg-zinc-900">
              <OrderGraph />
            </Card>
          </motion.div>

          <motion.div
              variants={bentoVariants}
              className="col-span-12"
          >
            {delivery ? <DeliveryMap deliveries={delivery.last_delivery}/> : <div className="h-[30vh] "><Loading icon={<span><TruckIcon className="text-primary"/> <MapIcon className="text-primary"/></span>}/></div>}

          </motion.div>

          {/* Logs Système (largeur totale) */}
          <motion.div
              variants={bentoVariants}
              className="col-span-12 row-span-1"
          >
            <Card shadow="sm" className="p-4 h-full dark:bg-zinc-900">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">Logs Système</h3>
                <FileText size={20} className="text-gray-500 dark:text-gray-100" />
              </div>
              {logs ? <ScrollShadow className="space-y-1 shadow-none">
                {logs.map((log) => (
                    <div
                        key={log.id}
                        className="flex items-center py-1 border-b last:border-none"
                    >
                      <AlertTriangle size={16} className="mr-2 text-red-500" />
                      <span className="text-sm">{log.message}</span>
                    </div>
                ))}
              </ScrollShadow> : <div className="h-[20vh] "><Loading icon={<Package className="text-primary"/>}/></div>}
            </Card>
          </motion.div>

        </motion.div>
      </div>
  );
}
